/**
 * Run an async operation on each item in parallel using `Promise.allSettled`,
 * then throw a summary error if any operations failed.
 *
 * Keeps selection intact on failure so the user can retry.
 */
export async function settleAll<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
): Promise<void> {
  const results = await Promise.allSettled(items.map(fn));
  const failed = results.filter((r) => r.status === "rejected").length;
  if (failed) {
    throw new Error(`${failed} of ${results.length} failed`);
  }
}

/**
 * `settleAll`, one item at a time: for writes the server must apply in
 * order (reporting-line changes, where one write can lift a member another
 * write also moves). Every item runs; the failures are counted and thrown
 * together, as `settleAll` does.
 */
export async function settleInOrder<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
): Promise<void> {
  let failed = 0;
  for (const item of items) {
    try {
      await fn(item);
    } catch {
      failed += 1;
    }
  }
  if (failed) {
    throw new Error(`${failed} of ${items.length} failed`);
  }
}
